// ════════════════════════════════════════════════════════════════════════════
//  Udisys Gestor Web  —  pages/Compras/Relatorios/BudgetCompras.tsx
//
//  Fase 3 · Iter 3.4 — CRUD de orçamento de compras (por mês × grupo).
// ════════════════════════════════════════════════════════════════════════════

import { useMemo, useState } from 'react';
import { Pencil, Plus, Target, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

import { useBudgets, useSalvarBudget } from '@/features/compras/hooks/useBudget';
import { useProductGroups } from '@/features/produtos/hooks/useProductGroups';
import type { CompraBudget, SalvarCompraBudgetRequest } from '@/features/compras/types';
import { getApiErrorMessage } from '@/lib/apiClient';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';

const fmt = (v: number) =>
  v.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const MESES = [
  'Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun',
  'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez',
];

const BudgetCompras = () => {
  const hoje  = new Date();
  const [ano, setAno] = useState(hoje.getFullYear());
  const { data: budgets = [], isLoading } = useBudgets(ano);
  const { groups: gruposProduto = [] } = useProductGroups();
  const { criar, atualizar, excluir, isSaving, isDeleting } = useSalvarBudget();

  const [dialogAberto, setDialogAberto] = useState(false);
  const [edit, setEdit] = useState<CompraBudget | null>(null);

  // form
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [grupoId, setGrupoId] = useState<number | null>(null);
  const [valor, setValor] = useState('');
  const [obs, setObs] = useState('');

  const totalAno = useMemo(() => budgets.reduce((a, b) => a + b.valorPlanejado, 0), [budgets]);
  const porMes = useMemo(() => {
    const mapa = new Map<number, number>();
    for (const b of budgets) mapa.set(b.mes, (mapa.get(b.mes) ?? 0) + b.valorPlanejado);
    return mapa;
  }, [budgets]);

  const abrirNovo = () => {
    setEdit(null);
    setMes(hoje.getMonth() + 1);
    setGrupoId(null);
    setValor('');
    setObs('');
    setDialogAberto(true);
  };

  const abrirEdicao = (b: CompraBudget) => {
    setEdit(b);
    setMes(b.mes);
    setGrupoId(b.grupoProdutoId);
    setValor(b.valorPlanejado.toString());
    setObs(b.observacao ?? '');
    setDialogAberto(true);
  };

  const salvar = async () => {
    if (!grupoId) { toast.error('Selecione um grupo de produto.'); return; }
    const v = Number(valor.replace(',', '.'));
    if (!Number.isFinite(v) || v < 0) { toast.error('Valor planejado inválido.'); return; }

    const req: SalvarCompraBudgetRequest = {
      ano, mes, grupoProdutoId: grupoId, valorPlanejado: v,
      observacao: obs || undefined,
    };

    try {
      if (edit) {
        await atualizar({ id: edit.id, req });
        toast.success('Orçamento atualizado.');
      } else {
        await criar(req);
        toast.success('Orçamento criado.');
      }
      setDialogAberto(false);
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  const deletar = async (id: number) => {
    if (!confirm('Excluir este orçamento?')) return;
    try {
      await excluir(id);
      toast.success('Orçamento removido.');
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex justify-between items-start">
        <div>
          <h1 className="text-3xl font-bold">Orçamento de Compras</h1>
          <p className="text-muted-foreground mt-1">
            Defina metas mensais de gasto por grupo de produto. O comparativo
            Budget × Realizado é calculado automaticamente a partir dos recebimentos.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-32">
            <Label className="text-xs">Ano</Label>
            <Input
              type="number"
              value={ano}
              onChange={(e) => setAno(Number(e.target.value) || ano)}
              min={2000}
              max={2100}
            />
          </div>
          <Button className="gap-2 mt-6" onClick={abrirNovo}>
            <Plus className="h-4 w-4" /> Novo orçamento
          </Button>
        </div>
      </div>

      {/* Resumo mensal */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Target className="h-4 w-4" /> Resumo de {ano}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-6 md:grid-cols-12 gap-2 text-center text-xs">
            {MESES.map((m, i) => (
              <div key={m} className="p-2 rounded-md bg-muted/30">
                <div className="text-muted-foreground">{m}</div>
                <div className="font-semibold mt-1">
                  {(porMes.get(i + 1) ?? 0) > 0
                    ? fmt(porMes.get(i + 1)!)
                    : <span className="text-muted-foreground">—</span>}
                </div>
              </div>
            ))}
          </div>
          <div className="mt-4 flex items-center justify-end gap-2 text-sm">
            <span className="text-muted-foreground">Total do ano:</span>
            <span className="font-bold text-lg">{fmt(totalAno)}</span>
          </div>
        </CardContent>
      </Card>

      {/* Tabela */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <Skeleton className="h-40 w-full" />
          ) : budgets.length === 0 ? (
            <div className="p-4 rounded-md bg-muted/30 text-sm text-muted-foreground">
              Nenhum orçamento definido para {ano}. Clique em "Novo orçamento".
            </div>
          ) : (
            <div className="rounded-md border overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Mês</TableHead>
                    <TableHead>Grupo de Produto</TableHead>
                    <TableHead className="text-right">Planejado</TableHead>
                    <TableHead>Observação</TableHead>
                    <TableHead></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {budgets.map((b) => (
                    <TableRow key={b.id}>
                      <TableCell className="text-sm">{MESES[b.mes - 1]}/{b.ano}</TableCell>
                      <TableCell>{b.grupoProdutoNome ?? `#${b.grupoProdutoId}`}</TableCell>
                      <TableCell className="text-right font-semibold">
                        {fmt(b.valorPlanejado)}
                      </TableCell>
                      <TableCell className="text-xs max-w-[300px] truncate" title={b.observacao ?? ''}>
                        {b.observacao ?? '—'}
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => abrirEdicao(b)}>
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            disabled={isDeleting}
                            onClick={() => deletar(b.id)}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog criar/editar */}
      <Dialog open={dialogAberto} onOpenChange={setDialogAberto}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{edit ? 'Editar orçamento' : 'Novo orçamento'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label>Ano</Label>
                <Input
                  type="number"
                  value={ano}
                  disabled
                />
              </div>
              <div className="space-y-1">
                <Label>Mês *</Label>
                <Select value={String(mes)} onValueChange={(v) => setMes(Number(v))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {MESES.map((m, i) => (
                      <SelectItem key={m} value={String(i + 1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label>Grupo de Produto *</Label>
              <Select
                value={grupoId ? String(grupoId) : ''}
                onValueChange={(v) => setGrupoId(Number(v))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione…" />
                </SelectTrigger>
                <SelectContent>
                  {gruposProduto.map((g) => (
                    <SelectItem key={g.id} value={String(g.id)}>
                      {g.descricao ?? `#${g.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label>Valor planejado *</Label>
              <Input
                type="number"
                step="0.01"
                value={valor}
                onChange={(e) => setValor(e.target.value)}
                placeholder="Ex: 50000.00"
              />
            </div>

            <div className="space-y-1">
              <Label>Observação</Label>
              <Textarea
                rows={3}
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                placeholder="Ex: reposição de estoque da linha X"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="ghost" onClick={() => setDialogAberto(false)}>Cancelar</Button>
            <Button onClick={salvar} disabled={isSaving}>
              {isSaving ? 'Salvando…' : 'Salvar'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BudgetCompras;
